/**
 * Wire shapes for setting a register up with a rotating code (#263, screen added by #469 step 5, made
 * account-free and rotating by #279).
 *
 * The issue response carries the **material** for the codes — a session id and a shared secret — not a code.
 * The secret is the only thing that lets a browser produce valid codes, so it is held in memory by the open
 * panel and nowhere else — see `EnrollmentCodePanelComponent`.
 *
 * Since #279 a code names **no account**: it binds to the company + shard it was issued for, and the register
 * redeeming it receives a token whose roles are exactly `[Register]`. There is therefore no account shape in
 * this file any more — the former `DeviceEnrollmentAccount` / `DeviceRegisterAccountDraft` and the
 * `/device/enrollment/accounts` endpoints they rode are retired.
 */

/**
 * The opened enrolment session. Mirrors `API.Core.Models.Device.DeviceEnrollmentIssueResult`.
 *
 * Nothing in here is a code. The browser derives the rotating code from `secret` + `windowSeconds` every window
 * (see `rotating-enrollment-code.ts`), wraps it with `sessionId`, and builds the QR itself — the server returns
 * no image and no single string, so nothing on screen can ever be "the expired one".
 */
export interface DeviceEnrollmentSession {
  /** The session's id, carried in the QR so redemption routes straight to the row. Not a secret. */
  sessionId: string;
  /**
   * The base32 shared secret the rotating code is computed from. Returned exactly once, kept **only in the
   * memory of the open panel** — never in a store, never in `localStorage` (which `CookieService` traps anyway),
   * never in a log line.
   */
  secret: string;
  /** The server the QR points a register at. Shown so an operator can see where the QR sends the till. */
  host: string;
  /** Seconds each code stays current — the rotation cadence, and the length of the draining bar. */
  windowSeconds: number;
  /** ISO-8601 UTC instant at which the session stops being redeemable — the hard backstop behind the codes. */
  expiration: string;
}

/**
 * How long a freshly opened session lives, in seconds. Mirrors `DeviceEnrollmentCode.DefaultSessionLifetime` on
 * the server (ten minutes), and is used **only** as the fallback when the server's `expiration` cannot be read
 * against this machine's clock — the authority on expiry is always the server.
 */
export const ENROLLMENT_SESSION_LIFETIME_SECONDS = 600;

/**
 * The German sentence for a structured refusal, keyed on `ApiError.code`.
 *
 * Per the style guide, the backend's English `description` is developer-facing and must never be rendered —
 * so every code this screen can provoke gets an explicit sentence here, and anything unrecognised falls back
 * to {@link ENROLLMENT_ERROR_FALLBACK} rather than to the raw field.
 *
 * A code is **Standort**-scoped (a shard), which is the word every sentence here uses. "Verkaufspunkt" is the
 * name of a *location* inside a shard — the device cards use it for exactly that — and calling the shard by it
 * sent the reader to the wrong screen.
 */
const ENROLLMENT_ERRORS: Record<string, string> = {
  E_DEVICE_ENROLLMENT_FORBIDDEN_SHARD:
    'Sie dürfen Kassen nur für Ihren eigenen Standort einrichten.',
  // The residual tenant refusal (#279): the code binds to the caller's company + shard, and this is the
  // server's answer when that pair cannot be resolved into one a register may be set up for. There is no
  // account to pick differently any more, so the remedy is a fresh session, not a different entry.
  E_DEVICE_ENROLLMENT_USER_NOT_ELIGIBLE:
    'Für diesen Standort kann derzeit keine Kasse eingerichtet werden. '
    + 'Bitte melden Sie sich erneut an und versuchen Sie es noch einmal.',
  E_DEVICE_ENROLLMENT_CODE_INVALID:
    'Dieser Einrichtungscode gilt nicht mehr. Bitte lassen Sie einen neuen anzeigen.'
};

/** Shown for a refusal without an explicit sentence, so no English ever reaches the screen. */
export const ENROLLMENT_ERROR_FALLBACK =
  'Der Code konnte nicht angezeigt werden. Bitte versuchen Sie es erneut.';

/**
 * Maps a structured error code to its German sentence, falling back rather than rendering `description`.
 *
 * @param code the `ApiError.code`, if the refusal carried one.
 */
export function enrollmentErrorMessage(code: string | undefined | null): string {
  return (code && ENROLLMENT_ERRORS[code]) || ENROLLMENT_ERROR_FALLBACK;
}

/**
 * The German sentence for an enrolment that **failed after the code was already redeemed** (#647), keyed on the
 * `EnrollmentFailed` push's `errorCode`.
 *
 * This is a second vocabulary rather than more entries in {@link ENROLLMENT_ERRORS} because the two describe
 * different moments and therefore need different remedies. The table above answers *"the code could not be
 * shown"* — nothing happened, press again. This one answers *"the till consumed the code and the login behind
 * it then failed"*: the code is spent, the register is showing an error of its own, and the operator's next
 * step is almost never "press again" (a device cap is not cleared by retrying).
 *
 * The codes are of two shapes on purpose, because the server has two: a structured `ApiError.code` (`E_…`) from
 * a catalog, and the **name** of a legacy `ResponseErrors` member for the refusals that still answer with a bare
 * enum — `MaxDeviceCountReached` (the contract device cap, the single most likely failure here; see
 * `ClientProcessor._checkRegistrationCount`) is the latter. Both are matched, so neither reaches the screen as
 * English, and an unknown code degrades to {@link ENROLLMENT_FAILURE_FALLBACK}, never to `description`.
 */
const ENROLLMENT_FAILURES: Record<string, string> = {
  // The contract cap, refused before the first provider round-trip. Retrying cannot help — a slot has to be
  // freed or the contract widened — so the sentence names both remedies rather than inviting another attempt.
  MaxDeviceCountReached:
    'Die in Ihrem Vertrag vereinbarte Anzahl an Kassen ist erreicht. '
    + 'Bitte geben Sie eine nicht mehr genutzte Kasse frei oder erweitern Sie Ihren Vertrag.',
  // The lock has to come off first, and only then is a fresh code any use — the one on screen is spent, so
  // "versuchen Sie es erneut" would be an instruction the screen cannot carry out.
  Locked:
    'Diese Kasse ist gesperrt. Bitte heben Sie die Sperre in der Geräteübersicht auf '
    + 'und lassen Sie anschließend einen neuen Code anzeigen.',
  // The tenant could not be resolved for the device behind the code. A fresh session is the remedy: the spent
  // one cannot be reused either way.
  ShardVerificationError:
    'Die Kasse konnte diesem Standort nicht zugeordnet werden. Bitte lassen Sie einen neuen Code anzeigen.',
  CompanyVerificationError:
    'Die Kasse konnte diesem Unternehmen nicht zugeordnet werden. Bitte lassen Sie einen neuen Code anzeigen.',
  // The server's own fallback: the login behind the redeem was refused without a named reason (an inactive
  // Standort, a locked device). It names the two things an administrator can check from here, rather than
  // repeating "es ist etwas schiefgelaufen" — which is what the generic fallback below already says.
  E_DEVICE_ENROLLMENT_LOGIN_FAILED:
    'Die Anmeldung der Kasse wurde abgelehnt. '
    + 'Prüfen Sie, ob der Standort aktiv und das Gerät nicht gesperrt ist.'
};

// Deliberately NOT mapped here: `E_DEVICE_ENROLLMENT_CODE_INVALID`. A refused code publishes nothing at all —
// there is no redeem, so there is no session to report a failure for — and it therefore cannot reach this
// table. It stays in `ENROLLMENT_ERRORS` above, where the issue call really can answer it.

/**
 * Shown when an enrolment fails with a code this build does not know. It deliberately sends the operator to the
 * **register's own screen**: that device holds the provider's real reason, and this screen must never invent one.
 */
export const ENROLLMENT_FAILURE_FALLBACK =
  'Die Kasse konnte nicht eingerichtet werden. Bitte prüfen Sie die Anzeige auf der Kasse '
  + 'und lassen Sie anschließend einen neuen Code anzeigen.';

/**
 * Maps an `EnrollmentFailed` error code to its German sentence.
 *
 * @param code the push's `errorCode` — an `ApiError` code or a `ResponseErrors` member name.
 */
export function enrollmentFailureMessage(code: string | undefined | null): string {
  return (code && ENROLLMENT_FAILURES[code]) || ENROLLMENT_FAILURE_FALLBACK;
}

/** Confirmation shown beside the rename field once the new name is in force. */
export const DEVICE_RENAME_SAVED = 'Name gespeichert.';

/** A device of that name already exists on the shard — the server's 409 / `AlreadyExisting`. */
export const DEVICE_RENAME_CONFLICT = 'Dieser Name ist bereits vergeben.';

/** The caller may not rename this device (403) — e.g. the permission was withdrawn since the card was drawn. */
export const DEVICE_RENAME_FORBIDDEN = 'Sie dürfen den Namen dieser Kasse nicht ändern.';

/** Anything else. A rename is trivially repeatable, so the sentence simply invites another attempt. */
export const DEVICE_RENAME_FALLBACK =
  'Der Name konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.';

/**
 * The German sentence for a failed `Device/Information/ChangeName`.
 *
 * `DeviceService.ChangeName` answers a **bare `ResponseErrors` enum** on a duplicate name
 * (`ResponseModel.Conflict(ResponseErrors.AlreadyExisting)`) — it carries no `ErrorCode`, so `BuildResponse`
 * emits that value itself rather than the structured `{code, description}` body. Whether it serialises as the
 * string `"AlreadyExisting"` or as its ordinal therefore depends on the host's JSON options, which is exactly
 * the kind of thing a screen must not depend on. **The status is the reliable signal**: 409 is the only
 * conflict this endpoint produces, so it is read first, and the body is consulted only as a second reading
 * (which would also catch a future migration of this path onto an `E_…` code).
 *
 * @param err whatever rxjs handed the `error` callback — an `HttpErrorResponse` in practice.
 */
export function deviceRenameErrorMessage(err: unknown): string {
  const failure = err as {status?: number; error?: unknown} | null;
  const body = failure?.error;
  const code = typeof body === 'string' ? body : (body as {code?: unknown} | null | undefined)?.code;

  if (failure?.status === 409 || code === 'AlreadyExisting') return DEVICE_RENAME_CONFLICT;
  if (failure?.status === 403) return DEVICE_RENAME_FORBIDDEN;
  return DEVICE_RENAME_FALLBACK;
}
